/**
 * Group Thread Tags — pure filter predicate (domain logic, shared by the
 * service `listByFilter` and the UI `ThreadTagFilterBar`). No I/O, no access
 * checks — visibility/fail-closed is the caller's job (the service filters to
 * readable threads BEFORE applying this predicate).
 */
import { priorityRank } from './taxonomy';
import type { ThreadTagFilter, ThreadTagState } from './types';

/** The subset of tag state a filter reads. */
export type FilterableTags = Pick<
  ThreadTagState,
  'status' | 'ownerUserId' | 'priority' | 'workflowLabels'
>;

export function threadMatchesTagFilter(tags: FilterableTags, filter: ThreadTagFilter): boolean {
  if (filter.status && filter.status.length > 0 && !filter.status.includes(tags.status)) {
    return false;
  }
  // `ownerUserId: null` filters for UNASSIGNED; `undefined` means "don't filter".
  if (filter.ownerUserId !== undefined && (filter.ownerUserId ?? null) !== (tags.ownerUserId ?? null)) {
    return false;
  }
  if (filter.minPriority && priorityRank(tags.priority) < priorityRank(filter.minPriority)) {
    return false;
  }
  if (filter.labels && filter.labels.length > 0) {
    const owned = new Set(tags.workflowLabels);
    for (const label of filter.labels) {
      if (!owned.has(label)) return false;
    }
  }
  return true;
}

export function filterThreadsByTags<T extends FilterableTags>(
  items: readonly T[],
  filter: ThreadTagFilter,
): T[] {
  return items.filter((item) => threadMatchesTagFilter(item, filter));
}
